import { Card, Divider, Form } from 'antd';
import { AppLogo } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useLoginMutation } from 'entities/User';
// import { GoogleAuthButton } from 'features/GoogleAuthButton';
import { YandexAuthButton } from 'features/YandexAuthButton';
import { FC, memo, useCallback } from 'react';
import { Button } from 'shared/ui/Button';
import { Checkbox } from 'shared/ui/Checkbox';
import { ModernInput as Input } from 'shared/ui/ModernInput';
import { ModernInputPassword } from 'shared/ui/ModernInputPassword';
import { isErrorApiResponse } from 'shared/utils/helpers/isErrorApiResponse';
import { logInModalActions } from '../model/slice/loginModalSlice';
import { LogInModalValues } from '../model/types';
import { VkAuthButton } from 'features/VkAuthButton';
import classNames from 'classnames';

interface AuthFormProps {
  className?: string;
}

export const AuthForm: FC<AuthFormProps> = memo((props) => {
  const { className } = props;

  const { t } = useAppTranslation(['login-page', 'common', 'booking']);
  const dispatch = useAppDispatch();

  const [login, { isLoading }] = useLoginMutation();

  const [form] = Form.useForm();

  const closeModal = useCallback((): void => {
    dispatch(logInModalActions.setOpened(false));
  }, [dispatch]);

  const openForgotPasswordModal = useCallback((): void => {
    dispatch(logInModalActions.changeMode('forgotPassword'));
  }, [dispatch]);

  const submit = useCallback(
    async (values: LogInModalValues): Promise<void> => {
      try {
        const { email, password, rememberUser } = values;

        await login({ loginData: { email, password }, remember: rememberUser }).unwrap();
        closeModal();
      } catch (error: CustomAny) {
        if (isErrorApiResponse(error)) {
          const errorMessage = Array.isArray(error.data.message)
            ? error.data.message.map((message) => t(message, { ns: 'common' }))
            : t(error.data.message, { ns: 'common' });
          showNotification('error', t('Error', { ns: 'common' }), errorMessage);
        } else {
          console.log(error);
        }
      }
    },
    [closeModal, login, t],
  );

  const handleSuccessGoogleAuth = useCallback((): void => {
    closeModal();
  }, [closeModal]);

  const handleFailGoogleAuth = useCallback(
    (error: string): void => {
      if (isErrorApiResponse(error)) {
        showNotification('error', t('Error', { ns: 'common' }), t(error.data.message));
      } else {
        console.log(error);
      }
    },
    [t],
  );

  return (
    <Form name="login" layout="vertical" initialValues={{ rememberUser: true }} form={form} autoComplete="off" onFinish={submit}>
      <Card
        className={classNames('bg-transparent border-none pt-7 pb-1', className)}
        title={
          <div className="flex flex-col justify-center items-center">
            <div className="flex items-center">
              <AppLogo width={180} height="100%" className="w-[180px]" />
            </div>
            <div className="text-3xl font-semibold mt-4 font-tektur">{t('Log in')}</div>
          </div>
        }
      >
        <div className="flex gap-4 items-center justify-center">
          <YandexAuthButton onSuccessAuth={handleSuccessGoogleAuth} onFailAuth={handleFailGoogleAuth} />
          <VkAuthButton className="flex gap-4 items-center" onSuccessAuth={handleSuccessGoogleAuth} onFailAuth={handleFailGoogleAuth} />
        </div>
        <Divider className="!border-grey !text-grey">{t('OR')}</Divider>
        <Form.Item
          name="email"
          required={false}
          label={<span className="text-2xl font-tektur">{t('Email', { ns: 'booking' })}</span>}
          rules={[
            { required: true, message: t('Please enter your email!') },
            { type: 'email', message: t('Please enter correct email!') },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="password"
          required={false}
          label={<span className="text-2xl font-tektur">{t('Password', { ns: 'booking' })}</span>}
          rules={[
            { required: true, message: t('Please enter your password!') },
            { min: 8, message: t('Password must be at least 8 characters!') },
          ]}
        >
          <ModernInputPassword />
        </Form.Item>
        <div className="flex justify-between items-center mb-6">
          <Form.Item name="rememberUser" valuePropName="checked" className="mb-0">
            <Checkbox name="rememberUser" defaultChecked>
              <span className="text-grey">{t('Remember me')}</span>
            </Checkbox>
          </Form.Item>
          <div className="text-black cursor-pointer underline" onClick={openForgotPasswordModal}>
            {t('Forgot password?')}
          </div>
        </div>
        <Button type="submit" containerClassName="w-full" isLoading={isLoading}>
          {t('Login')}
        </Button>
      </Card>
    </Form>
  );
});
